// #522 — every class list shows the class NUMBER and sorts by it numerically.
// The class number (`classNumber`) is stamped from the FVD day sheet on
// reconcile; `className` is the class name.

// #1805 — two different constants, deliberately not one. scheduledTime is
// an FVD Access stamp, not a genuine UTC instant (#1811): FVD's Access DB
// holds a naive venue wall-clock time, ADODB on `fvdts`
// (America/Los_Angeles) parses that as box-local, and foxbox reads it back
// with UTC getters. Rendering an FVD stamp back through FVD_STAMP_TIMEZONE is
// the exact inverse of that mangling, which is the only reason these times
// have ever displayed correctly (the #612 "PST stopgap" comments below were
// load-bearing, not a leftover). A genuine server instant (e.g.
// updatedAt/`new Date().toISOString()`) has no such mangling to invert, so it
// belongs on the show's real venueTimezone instead — DEFAULT_VENUE_TIMEZONE
// is that resolver's Pacific fallback, reused here rather than a duplicate
// local literal, since this file has no show.venueTimezone wired in yet
// (no urgency: Jaybill, 2026-08-21 on #1805, these renderers are "correct by
// cancellation"). Do NOT swap either use for the other's constant — that is
// exactly the #1805 3-hour regression #1811 headed off.
import { FVD_STAMP_TIMEZONE, DEFAULT_VENUE_TIMEZONE } from "common-fe/venueTime";

// #1665 — was score-common/catalog's getCatalogTestById/getRideTestName,
// synchronous lookups over the build-time bundle. That bundle is retired
// (docs/score/catalog-audit-findings.md section 9); every caller here now
// passes an explicit `catalogDefsById` Map, built from the LIVE score-catalog
// table (GET /catalog, card #1662's useCatalogDefs hook / IndexedDB cache),
// keyed by def id. Returns null exactly where the bundle-backed version did:
// missing id, missing def, or (new failure mode, since the table is fetched
// async) the cache not yet hydrated.

/** Direct lookup by our own test def id against an explicit catalogDefsById
 * Map. */
export function getCatalogTestById(id, catalogDefsById) {
  if (id == null) return null;
  return catalogDefsById?.get(id) ?? null;
}

/** #915 — resolve the display name of the test a RIDE is riding, off
 * ride.testId against catalogDefsById (see getCatalogTestById above).
 * Resolution is always PER RIDE, never per class: Test-of-Choice classes let
 * different rides carry different testIds, so callers must pass the ride,
 * not the class. Returns null when the ride, its testId, or the catalog def
 * is missing/not yet hydrated. */
export function getRideTestName(ride, catalogDefsById) {
  return getCatalogTestById(ride?.testId, catalogDefsById)?.name ?? null;
}

// "12. Training Level Test 1" when a class number is present, else just the name.
export function classNumberLabel(c) {
  const n = c?.classNumber;
  const name = c?.className || c?.id || "";
  return n != null && `${n}`.trim() !== "" ? `${n}. ${name}` : name;
}

const leadingInt = (v) => {
  const m = /^\s*(\d+)/.exec(v == null ? "" : String(v));
  return m ? parseInt(m[1], 10) : NaN;
};

// Numeric-first comparator: classes with a numeric class number sort ascending
// by it (tie-break on the raw string, e.g. "12" before "12A"); classes with no
// number sort after them, keeping their incoming order (returns 0 → stable).
export function compareByClassNumber(a, b) {
  const na = leadingInt(a?.classNumber);
  const nb = leadingInt(b?.classNumber);
  const aOk = !Number.isNaN(na);
  const bOk = !Number.isNaN(nb);
  if (aOk && bOk) {
    return na - nb || String(a.classNumber).localeCompare(String(b.classNumber));
  }
  if (aOk) return -1;
  if (bOk) return 1;
  return 0;
}

// Stable numeric sort of a classes array (never mutates the input).
export function sortByClassNumber(classes) {
  return (classes ?? []).slice().sort(compareByClassNumber);
}

// #573/#591 — a class's scheduled start time as a short clock time. scheduledTime
// is an FVD-stamped string; render an ISO datetime in UTC (FVD stamps the naive
// local wall-clock with a "Z", so UTC shows the intended ring time regardless of
// the viewer's zone), and leave an already-formatted string ("8:30 AM") untouched.
export function formatClassStartTime(v) {
  if (!v) return null;
  const d = new Date(v);
  if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(v)) {
    // #612 / #1805 — render the FVD stamp back through FVD_STAMP_TIMEZONE
    // (the shared #1811 constant, was a local "America/Los_Angeles" literal).
    // Load-bearing, not a stopgap left over from before venueTimezone
    // existed: see the header comment above.
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: FVD_STAMP_TIMEZONE });
  }
  return String(v);
}

// #1012 — venue-local formatter for a genuine server timestamp (e.g. Publish
// Results' per-class federation-submission-state `updatedAt`, a real
// `new Date().toISOString()` instant). Unlike scheduledTime/classDayKey below
// (FVD's naive-local wall clock naively stamped with a "Z", which must be
// string-sliced, never Date-parsed), a server-stamped ISO timestamp IS a true
// UTC instant, so it belongs on the show's REAL venueTimezone (#1811 split),
// not the fixed FVD stamp zone formatClassStartTime uses above. This file has
// no show.venueTimezone threaded in yet (#1805 — no urgency), so it renders
// through DEFAULT_VENUE_TIMEZONE, the shared resolver's Pacific fallback —
// unchanged behavior from the old local "America/Los_Angeles" literal.
export function formatVenueLocalDateTime(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short", timeZone: DEFAULT_VENUE_TIMEZONE });
}

// #611 — real ingested classes carry no `day` field (reconcile never populates
// one); they only have `scheduledTime`. So day grouping found no day on any
// class and dumped everything under "No day". Derive a short weekday from
// scheduledTime, through FVD_STAMP_TIMEZONE (#1805/#1811 — an FVD stamp, not
// a stopgap), when `day` is absent, so classes group by day correctly.
// Prefer an explicit `day` if one is ever present.
export function classDay(c) {
  if (!c) return null;
  if (c.day) return c.day;
  const v = c.scheduledTime || c.time;
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString([], { weekday: "short", timeZone: FVD_STAMP_TIMEZONE });
}

// #634 — the day KEY a report is run for: the naive `YYYY-MM-DD` slice of
// scheduledTime, which is exactly what the day-scoped report defs filter on
// (packages/report-defs/src/score/class-day.js). It must stay a string slice:
// FVD stamps naive-local times with a "Z" (#590/#592), so parsing it as a Date
// and reading the local date back out would shift the day. classDay() above is
// the *display* grouping label and is deliberately left alone.
export function classDayKey(c) {
  const v = c?.scheduledTime || c?.time;
  if (!v) return null;
  const key = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
}

// "Fri, Jul 17" for a `YYYY-MM-DD` key — formatted at midday so no timezone can
// roll it to the adjacent day.
export function formatDayKey(key) {
  if (!key) return "";
  const d = new Date(`${key}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(key);
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
